/**
 * Toast — transient, non-blocking notifications. Supersedes the app's first
 * ad-hoc ToastProvider with a token-styled primitive that keeps the exact
 * same context API (push + tone/title/description), so every existing
 * consumer keeps working:
 *
 *   const toast = useToast();
 *   toast.push({ tone: 'success', title: 'Saved', description: 'Revision 42' });
 *
 * Announced through a polite live region; toasts never take focus. Skins are
 * the status token sets (subtle bg + ink text + line border) so both themes
 * pass contrast without per-tone styles. The fixed region reserves its
 * corner without owning it (UX-P7): `pointer-events-none` on the region so
 * clicks fall through to the page beneath (composer, scrollbar), inset
 * bottom/right 16px to clear the scrollbar; only the toast cards take
 * pointers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, Info, TriangleAlert, X } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Tone = "info" | "success" | "warning" | "danger";

/** One inline action a toast offers (CORE-248 undo). The action runs
 *  and dismisses; it never extends the toast's lifetime. */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

interface Toast {
  id: number;
  tone: Tone;
  title: string;
  description?: string;
  action?: ToastAction;
  durationMs?: number;
}

interface ToastContextValue {
  push: (toast: {
    tone: Tone;
    title: string;
    description?: string;
    action?: ToastAction;
    /** Override the 8s default (e.g. a bounded undo window). */
    durationMs?: number;
  }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_CLASSES: Record<Tone, string> = {
  info: "border-transparent bg-bg-300 text-ink-200",
  success: "border-status-done/40 bg-status-done-soft text-status-done",
  warning:
    "border-status-progress/40 bg-status-progress-soft text-status-progress",
  danger: "border-status-blocked/40 bg-status-blocked-soft text-status-blocked",
};

const TONE_ICONS: Record<Tone, ReactNode> = {
  info: <Info aria-hidden="true" className="h-4 w-4 shrink-0 text-ink-200" />,
  success: (
    <CheckCircle2
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-status-done"
    />
  ),
  warning: (
    <TriangleAlert
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-status-progress"
    />
  ),
  danger: (
    <TriangleAlert
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-status-blocked"
    />
  ),
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  // Pending auto-dismiss handles. An unmount cancels them: a timer
  // firing after the host is gone (a test environment tearing down, a
  // screen navigating away) would set state on a dead tree and, under
  // jsdom, crash on a disposed window (surfaced by CORE-321's CI).
  const timers = useRef<number[]>([]);

  useEffect(
    () => () => {
      for (const handle of timers.current) window.clearTimeout(handle);
    },
    [],
  );

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    ({
      tone,
      title,
      description,
      action,
      durationMs,
    }: {
      tone: Tone;
      title: string;
      description?: string;
      action?: ToastAction;
      durationMs?: number;
    }) => {
      const id = nextId.current++;
      // Keep at most the four newest toasts; oldest slide out.
      setToasts((current) => [
        ...current.slice(-3),
        { id, tone, title, description, action, durationMs },
      ]);
      // (A handle whose toast was already dismissed just lingers in
      // the array — clearing a fired timer is a no-op.)
      timers.current.push(
        window.setTimeout(
          () => dismiss(id),
          // A toast carrying an action never lingers past its window: a
          // stale action button is worse than a vanished message.
          durationMs ?? (action ? 10_000 : 8000),
        ),
      );
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-label="Notifications"
        className="pointer-events-none fixed bottom-4 right-4 z-toast flex w-80 max-w-[calc(100vw-1.5rem)] flex-col gap-2"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              "pointer-events-auto flex items-start gap-2.5 rounded-md border p-3 shadow-popover",
              "animate-in slide-in-from-bottom-2 fade-in-0 duration-move ease-standard",
              TONE_CLASSES[toast.tone],
            )}
          >
            {TONE_ICONS[toast.tone]}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-5">{toast.title}</p>
              {toast.description ? (
                // No opacity dial here: opacity-90 dropped the success
                // pair to 4.21:1 (found by the CORE-235 mobile axe
                // scan). Secondary weight comes from the regular font
                // weight against the medium title, not alpha.
                <p className="mt-0.5 text-sm leading-5">{toast.description}</p>
              ) : null}
              {toast.action ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 min-h-11"
                  onClick={() => {
                    toast.action?.onAction();
                    dismiss(toast.id);
                  }}
                >
                  {toast.action.label}
                </Button>
              ) : null}
            </div>
            <IconButton
              size="icon-sm"
              label={`Dismiss notification: ${toast.title}`}
              onClick={() => dismiss(toast.id)}
              className="-m-1 h-7 w-7 shrink-0 opacity-70 hover:opacity-100"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside <ToastProvider>");
  return value;
}
